import { Stack, Button } from '@mui/material'
import useAccordionStyle from 'src/Libs/styles'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { InstallationConfigDto } from '../../../installation/api'
import { SETTINGS_ROUTES } from '../../general/constants'
import { useTypedSelector } from 'src/State/RootReducer'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { InformationBanner } from '../../general/components'
import { useStorage } from '../hooks'
import { StorageExplanationItemProps, StorageOptionSettingsBox } from '../components'
import { useTheme } from '@mui/system'
import { Loading } from '@e3dc-react/shell/Elements/Loading'
import { useSystemSettingsTitleAndBreadcrumbs } from 'src/Libs/system-settings'
import { BlankView, BlankViewImageVariants } from 'src/Components/blank-view'
import { BannerLocations, BannerQueue } from 'src/Libs/banners'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

interface StoragePageProps {
  installationId: number
  queryIsLoading: boolean
  installationConfig: Nullable<InstallationConfigDto>
  hasNoStorage: boolean
}
//#region Page

/** User-level storage page to manage storage settings */
const StoragePage: React.FC<StoragePageProps> = ({ hasNoStorage, installationId, queryIsLoading, installationConfig }) => {
  useSystemSettingsTitleAndBreadcrumbs({ pageTitleKey: 'framework.end-user.storage' })
  const { t } = useCustomTranslation()
  const navigate = useNavigate()
  const { classes } = useAccordionStyle()
  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()

  const activeStorage = useTypedSelector(state => state.storage.activeStorage)

  const { solarMode, boostMode, updateSolarMode, updateBoostMode, areMutationsLoading } = useStorage({
    installationId,
    installationConfig,
  })

  const isLoading = areMutationsLoading || queryIsLoading

  /** If the battery has priority and not the EVSE, the options are disabled and a message is displayed to the user to change the priorities. */
  const isEmsBatteryBeforeCarMode = installationConfig?.emsBatteryBeforeCarMode
  const areOptionsDisabled = !!(isLoading || isEmsBatteryBeforeCarMode || !userCanEdit)
  const { solarModeItem: solarModeDescriptions, boostModeItem: boostModeDescriptions } = useStorageOptionItems()

  /* redirect to solar priority*/
  const handleRedirect = (): void => {
    if (!installationId || !activeStorage) return
    navigate(`/installation/${installationId}/${activeStorage}/properties/${SETTINGS_ROUTES.solarPriority}`)
  }

  if (hasNoStorage) {
    return (
      <BlankView
        title={'systemSettings.storage.no-active-device.title'}
        explanation={'systemSettings.storage.no-active-device.explanation'}
        imageVariant={BlankViewImageVariants.Worried}
      />
    )
  }

  return (
    <>
      <BannerQueue location={BannerLocations.FlowSettingsStorageManagement} sx={{ mb: 2, mt: [2, 0] }} />
      <AccordionItem expanded title={t('user-settings.storage-page.title')} accordionDetailsSx={{ px: 4 }} className={classes.accordion}>
        <Stack gap={3}>
          {isEmsBatteryBeforeCarMode && (
            <InformationBanner
              message={t('user-settings.storage-page.warning-banner')}
              suffix={
                <Button variant="text" onClick={handleRedirect}>
                  {t('user-settings.storage-page.warning-link')}
                </Button>
              }
            />
          )}

          <StorageOptionSettingsBox
            title={t('user-settings.storage-page.label.options.solar-only-title')}
            description={t('user-settings.storage-page.solar-only-description')}
            onToggleButtonAction={updateSolarMode}
            activeToggleButton={solarMode ? 1 : 0}
            disabled={areOptionsDisabled}
            options={solarModeDescriptions}
          />

          <StorageOptionSettingsBox
            title={t('user-settings.storage-page.label.options.boost-title')}
            description={t('user-settings.storage-page.boost-mode-description')}
            onToggleButtonAction={updateBoostMode}
            activeToggleButton={boostMode ? 1 : 0}
            disabled={areOptionsDisabled}
            options={boostModeDescriptions}
          />

          {isLoading && <Loading />}
        </Stack>
      </AccordionItem>
    </>
  )
}

export default StoragePage

//#endregion Page

//#region descriptionSettings helper
export interface StorageOptionItemsReturnType {
  solarModeItem: StorageExplanationItemProps[]
  boostModeItem: StorageExplanationItemProps[]
}
/** Descriptions of the options for the solar- and boost-mode */
const useStorageOptionItems = (): StorageOptionItemsReturnType => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const solarItem = {
    label: t('user-settings.storage-page.label.solar'),
    color: theme.palette.energyStorageManagementDescriptions?.solar,
  }
  const storageItem = {
    label: t('user-settings.storage-page.label.storage'),
    color: theme.palette.energyStorageManagementDescriptions?.storage,
  }
  const gridItem = {
    label: t('user-settings.storage-page.label.grid'),
    color: theme.palette.energyStorageManagementDescriptions?.grid,
  }
  return {
    solarModeItem: [
      {
        description: t('user-settings.storage-page.solar-only-option-no'),
        descriptionItems: [
          solarItem,
          {
            disabled: true,
            ...storageItem,
          },
        ],
      },
      {
        description: t('user-settings.storage-page.solar-only-option-yes'),
        descriptionItems: [solarItem, storageItem],
      },
    ],
    boostModeItem: [
      {
        description: t('user-settings.storage-page.boost-mode-option-no'),
        descriptionItems: [solarItem, gridItem],
      },
      {
        description: t('user-settings.storage-page.boost-mode-option-yes'),
        descriptionItems: [solarItem, storageItem, gridItem],
      },
    ],
  }
}
//#endregion
